/** Sidebar pin/unpin, remembered per user (localStorage key includes the user id). */
import { useCallback, useEffect, useState } from 'react';

export const pinKey = (userId: string | null | undefined) => `collab.sidebar.pinned.${userId ?? 'anon'}`;
export function readPinned(userId: string | null | undefined, storage: Pick<Storage, 'getItem'> = localStorage): boolean {
  try { return storage.getItem(pinKey(userId)) !== '0'; } catch { return true; }
}
export function writePinned(userId: string | null | undefined, pinned: boolean, storage: Pick<Storage, 'setItem'> = localStorage) {
  try { storage.setItem(pinKey(userId), pinned ? '1' : '0'); } catch { /* private mode */ }
}
/** Ctrl+\ (Cmd+\ on Mac) shows/hides the sidebar; the shortcut ignores modifiers like Shift/Alt. */
export const isSidebarShortcut = (e: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'altKey'>) => (e.ctrlKey || e.metaKey) && !e.altKey && e.key === '\\';

export function useSidebarPin(userId: string | null | undefined) {
  const [pinned, setPinnedState] = useState(() => readPinned(userId));
  const [peek, setPeek] = useState(false);
  useEffect(() => { setPinnedState(readPinned(userId)); }, [userId]);
  const setPinned = useCallback((v: boolean) => { writePinned(userId, v); setPinnedState(v); setPeek(false); }, [userId]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isSidebarShortcut(e)) { e.preventDefault(); if (!pinned) setPeek(p => !p); else setPinned(false); }
      else if (e.key === 'Escape' && peek) setPeek(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pinned, peek, setPinned]);
  return { pinned, setPinned, peek, setPeek };
}
